/* eslint-disable no-plusplus */
/* eslint-disable react/no-danger */
import { snakeCase } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import classNames from 'classnames';

import { Button, Icon } from '@components/atoms';
import { Main } from '@components/molecules';
import { useComponent } from '@hooks/useComponent';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const Home = memo(() => {
  const [allPageNumbers, setAllPageNumbers] = React.useState();

  const [isTakeQuestion, setIsTakeQuestion] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [steps, setSteps] = useState([]);
  const { getComponent } = useComponent();

  const [loadingPDF, setLoadingPDF] = useState(false);
  const [pdfBase64, setPdfBase64] = useState('');

  const onDocumentLoadSuccess = pdf => {
    const allPageNumbers = []; // array of numbers
    for (let p = 1; p < pdf.numPages + 1; p++) {
      allPageNumbers.push(p);
    }
    setAllPageNumbers(allPageNumbers);
  };

  const [form, setForm] = useState(null);

  const fetchQuestions = useCallback(async () => {
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/questions`);
    const json = await res.json();
    if (json.success) {
      setQuestions(json.questions);
    }
  }, []);

  const handleStartQuestion = useCallback(() => {
    setIsTakeQuestion(true);
  }, []);

  const handleBack = useCallback(() => {
    setSteps(prev => {
      const arr = [...prev];
      arr.pop();
      return arr;
    });
  }, []);

  const downloadPDF = useCallback(() => {
    const linkSource = `data:application/pdf;base64,${pdfBase64}`;
    const downloadLink = document.createElement('a');
    const fileName = 'Product Idea Analyzer Results.pdf';
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }, [pdfBase64]);

  const data = useMemo(() => {
    const skips = {
      Businesses: 'How close are you to the ideal user?',
      Consumers: 'What is your experience in this industry?',
    };
    const skipName = snakeCase(
      skips[form?.is_this_product_more_for_consumers_or_businesses?.label],
    );
    return questions.filter(x => x.name !== snakeCase(skipName));
  }, [form?.is_this_product_more_for_consumers_or_businesses, questions]);

  const filteredQuestions = useMemo(
    () => data.slice(steps.length, steps.length + 1),
    [data, steps.length],
  );

  const handleChange = useCallback(
    component => e => {
      const { name, value, category, label, pro, con } = e.target;
      if (component === 'TextInput') {
        const getValue = () => {
          const numValue = Number(value);
          if (numValue < 5)
            return {
              score: 20,
              pro: '',
              con: 'Selling a product for too cheap can make it very difficult to market with sustainable margins.',
            };
          if (numValue >= 5 && numValue < 15)
            return { score: 70, pro: '', con: '' };
          if (numValue >= 15 && numValue < 35)
            return {
              score: 100,
              pro: `$${numValue} is right in the sweet spot for a mass market product. At this price range you can work towards good margins and also not turn off too many customers.`,
              con: '',
            };
          if (numValue >= 35 && numValue < 100)
            return {
              score: 70,
              pro: `$${numValue} is a good price to sell the right product that people really need. Be prepared to spend a good portion of you revenue on marketing.`,
              con: '',
            };
          if (numValue >= 100 && numValue < 1000)
            return {
              score: 20,
              pro: '',
              con: 'Expensive products typically cost a lot of money to develop. Make sure you have a budget to support that before you start the product development process. You should also be prepared to spend a lot on marketing. The more expensive the product the more challenging marketing it will be.',
            };
          return {
            score: -100,
            pro: '',
            con: 'Very expensive products typically require enormous budgets to develop. Make sure you have a budget to support that before you start the product development process. You should also be prepared to spend a lot on marketing. Marketing high-value items requires a sophisticated sales system.',
          };
        };
        setForm(prev => {
          const { score, pro, con } = getValue();
          return {
            ...prev,
            [name]: { category, value, label, score, pro, con },
          };
        });
      } else {
        setForm(prev => ({
          ...prev,
          [name]: { category, value, label, pro, con },
        }));
      }
      if (steps.length + 1 !== questions.length) {
        if (component === 'Select') {
          return setSteps(prev => [...prev, name]);
        }
      }

      return null;
    },
    [questions.length, steps.length],
  );

  const currentProgress = useMemo(
    () => (steps.length * 100) / data.length,
    [data.length, steps.length],
  );

  const handleSubmit = useCallback(async () => {
    setLoadingPDF(true);
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/generate-pdf`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ form }),
      },
    );

    const content = await res.json();
    if (content.success) {
      setPdfBase64(content.base64);
      setSteps(prev => [...prev, Object.keys(form).pop()]);
      setLoadingPDF(false);
    }
  }, [form]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [steps]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  if (!isTakeQuestion) return <Main onStart={handleStartQuestion} />;

  if (!questions.length)
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <Icon size="10x" name="spinner" className="fa-spin text-cyan-600" />
        <p className="mt-5 text-cyan-700">Please Wait...</p>
      </div>
    );

  return (
    <div className="p-5">
      {steps.length !== data.length && (
        <div className="bg-slate-200 rounded">
          <div
            className="bg-cyan-600 h-1 rounded duration-500"
            style={{ width: `${currentProgress}%` }}
          />
        </div>
      )}
      <div className="mt-5">
        {filteredQuestions.map((x, index) => {
          const InputComponent = getComponent(x.component);
          return (
            <div key={x.name} className="w-full space-y-12">
              <div className="overflow-y-auto w-full">
                <div
                  key={x.name}
                  className="flex flex-col items-center h-full justify-between">
                  <div className="flex flex-col space-y-6 items-left w-full">
                    <div className="space-y-5">
                      <div
                        className="transition-opacity duration-500 text-cyan-700 font-medium text-2xl"
                        dangerouslySetInnerHTML={{ __html: x.title }}
                      />
                      <div
                        className="text-slate-500 text-sm text-left"
                        dangerouslySetInnerHTML={{ __html: x.description }}
                      />
                      <div className="flex flex-col lg:flex-row items-center justify-center">
                        {x.image && (
                          <div
                            className="lg:flex-1 bg-origin-padding w-96 h-80 bg-contain bg-no-repeat bg-center mb-4"
                            style={{ backgroundImage: `url(${x.image})` }}
                          />
                        )}
                        <div
                          className={classNames('flex-1 w-full', {
                            'w-96': x.typeText === 'short',
                          })}>
                          <InputComponent
                            {...x}
                            onChange={handleChange(x.component, index)}
                            value={form?.[x.name]?.value}
                            label={form?.[x.name]?.label}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-6 py-5">
                {steps.length ? (
                  <Button
                    onClick={handleBack}
                    icon="arrow-left"
                    disabled={loadingPDF}
                    className={classNames({
                      'bg-slate-300 hover:bg-slate-300 hover:text-slate-500 border-slate-400 text-slate-500':
                        loadingPDF,
                    })}>
                    Back
                  </Button>
                ) : null}
                {steps.length + 1 === data.length ? (
                  <Button
                    disabled={
                      !form?.[filteredQuestions[0]?.name]?.value || loadingPDF
                    }
                    className={classNames({
                      'bg-slate-300 hover:bg-slate-300':
                        !form?.[filteredQuestions[0]?.name]?.value ||
                        loadingPDF,
                    })}
                    variant="primary"
                    onClick={handleSubmit}
                    icon="arrow-right"
                    loading={loadingPDF}>
                    Submit
                  </Button>
                ) : (
                  <Button
                    disabled={!form?.[filteredQuestions[0]?.name]?.value}
                    className={classNames({
                      'bg-slate-300 hover:bg-slate-300':
                        !form?.[filteredQuestions[0]?.name]?.value,
                    })}
                    variant="primary"
                    onClick={() => setSteps(prev => [...prev, x.name])}
                    icon="arrow-right">
                    Next
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {pdfBase64 && (
          <div className="mx-auto flex flex-col items-center">
            <Document
              className="text-right mb-5"
              file={`data:application/pdf;base64,${pdfBase64}`}
              onLoadSuccess={onDocumentLoadSuccess}>
              <div className="flex w-full items-center justify-center mb-2 space-x-5">
                <div>
                  <Button
                    type="button"
                    className="space-x-2"
                    onClick={downloadPDF}>
                    <Icon name="download" size="lg" />
                    <span>Download</span>
                  </Button>
                </div>
              </div>
              {allPageNumbers
                ? allPageNumbers.map(pn => (
                    <Page
                      key={`page-${pn}`}
                      width={900}
                      height={800}
                      className="flex justify-center border-2 mx-12"
                      pageNumber={pn}
                    />
                  ))
                : undefined}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
});
